<template>
  <div class="b-settings__block p-6 mt-6">
    <h2 class="mb-4">{{ $t('CONTENT.settingsPage.logs') }}</h2>
    <div v-if="error" class="mb-2">
      <server-error :error="error"/>
    </div>
    <form v-if="isLoaded" class="b-form p-0" @submit.prevent="onSubmit">
      <div class="b-settings__block-box b-contract_row_flex">
        <div class="mr-4">
          <button class="b-btn" @click.prevent="getLastLogs">{{ $t('CONTENT.settingsPage.downloadLastLogs') }}</button>
        </div>
        <div class="mr-4">
          <button class="b-btn" @click.prevent="getAllLogs">{{ $t('CONTENT.settingsPage.downloadAllLogs') }}</button>
        </div>
        <div>
          <button class="b-btn" @click.prevent="getClosedPeriodLogs">{{ $t('CONTENT.settingsPage.downloadClosedPeriodLogs') }}</button>
        </div>
      </div>

      <div class="b-settings__block-report mt-5 p-5">
        <h2 class="mb-2">{{ $t('CONTENT.settingsPage.report') }}</h2>
        <div class="row b-form__row_type_flex-end">
          <div class="col-2">
            <label>{{ $t('CONTENT.settingsPage.lessee') }}:<span class="b-form__required"> *</span></label>
            <cool-select :list="allCounterparties"
                         option-value="id"
                         option-text="value"
                         :placeholder="$t('CONTENT.choose')"
                         v-model="formData.lesseeId"></cool-select>
          </div>
          <div class="col-2">
            <label>{{ $t('CONTENT.settingsPage.period') }}:<span class="b-form__required"> *</span></label>
            <datepicker
              :required="true"
              v-bind:class="{ 'b-datepicker__error': isFieldWithError('date', date) }"
              class="b-datepicker"
              v-model="periodStartDate"
              :disabled-date="disabledDatesForStartDate"
              :formatter="datepickerFormat"
            ></datepicker>
          </div>
          <div class="col-2">
            <datepicker
              :required="true"
              v-bind:class="{ 'b-datepicker__error': isFieldWithError('date', date) }"
              class="b-datepicker"
              :disabled-date="disabledDatesForEndDate"
              v-model="periodEndDate"
              :formatter="datepickerFormat"
            ></datepicker>
          </div>
          <div>
            <button class="b-btn"
                    @click.prevent="getExcelEventsReport"
                    :disabled="!formData.lesseeId || !formData.periodStart || !formData.periodEnd">
              {{ $t('CONTENT.settingsPage.create_report') }}
            </button>
          </div>
        </div>
      </div>
      <div class="mr-4 mt-4">
        <button class="b-btn" @click.prevent="validateRegularPayments">{{ $t('CONTENT.settingsPage.validateRegularityPayments') }}</button>
      </div>
    </form>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import changeNumericValue from "../../mixins/changeNumericValue";
import datepickerFormat from '@/mixins/datepickerFormat.mixin';
import dateConverter from "../../utils/converters/date.converter";
import ReportsAPI from "../../plugins/reports/api/ReportsAPI";
import LogAPI from "@/api/Log";
import ContractAggregate from "@/models/contract/ContractAggregate";
import response from "vue2-datepicker";
import ContractAggregateAPI from "@/api/ContractAggregateAPI";
import downloadFileMixin from "@/mixins/downloadFile";

export default {
  data() {
    return {
      error: null,
      currencyCode: null,
      yearsToAddToPerpetuity: null,
      amortizationType: null,
      isDisabledSaveBtn: true,
      availableAmortizationTypes: [
        {
          name: this.$t('CONTENT.settingsPage.daily_amortization'),
          type: 'daily_amortization'
        },
        {
          name: this.$t('CONTENT.settingsPage.monthly_amortization'),
          type: 'monthly_amortization'
        }
      ],
      formData: {
        lesseeId: null,
        periodStart: null,
        periodEnd: null,
      },
    }
  },
  mixins: [changeNumericValue, datepickerFormat, downloadFileMixin],
  computed: {
    ...mapGetters(['getCurrencyCode']),
    ...mapGetters(['getContractSettings']),
    ...mapGetters(['getCounterpartiresForContract']),
    periodStartDate: {
      get() {
        if (this.formData.periodStart !== null) {
          return dateConverter.convertToDate(this.formData.periodStart);
        } else {
          return this.formData.periodStart;
        }
      },
      set(newVal) {
        if (newVal !== null) {
          this.formData.periodStart = dateConverter.convertToString(newVal);
        } else {
          this.formData.periodStart = null;
        }
      }
    },
    periodEndDate: {
      get() {
        if (this.formData.periodEnd !== null) {
          return dateConverter.convertToDate(this.formData.periodEnd);
        } else {
          return this.formData.periodEnd;
        }
      },
      set(newVal) {
        if (newVal !== null) {
          this.formData.periodEnd = dateConverter.convertToString(newVal);
        } else {
          this.formData.periodEnd = null;
        }
      }
    },
    allCounterparties() {
      return this.getCounterpartiresForContract;
    },
    allCurrencyCode() {
      return this.getCurrencyCode;
    },
    picked() {
      return this.getContractSettings.amortizationType;
    },
    currentCurrencyCode: {
      get() {
        if (this.currencyCode !== null) {
          return this.currencyCode.toLowerCase();
        } else {
          this.currencyCode = this.getContractSettings.currencyCode;
          return this.currencyCode.toLowerCase();
        }
      },
      set(newVal) {
        this.currencyCode = newVal.toUpperCase();
        this.isDisabledSaveBtn = false;
      }
    },
    currentYearsToAddToPerpetuity: {
      get() {
        this.yearsToAddToPerpetuity = this.getContractSettings.yearsToAddToPerpetuity;
        return this.getContractSettings.yearsToAddToPerpetuity;
      },
      set(newVal) {
        this.yearsToAddToPerpetuity = newVal;
      }
    }
  },
  watch: {
    yearsToAddToPerpetuity(value, oldValue) {
      if (oldValue !== value && oldValue !== null) {
        this.isDisabledSaveBtn = false;
      }
    }
  },
  methods: {
    disabledDatesForStartDate(date) {
      if (this.formData.periodEnd !== null) {
        return date > dateConverter.convertToDate(this.formData.periodEnd);
      }
    },
    disabledDatesForEndDate(date) {
      if (this.formData.periodStart !== null) {
        return (
          date < dateConverter.convertToDate(this.formData.periodStart)
        );
      }
    },
    isFieldWithError(field, value) {
      return !value && this.validationErrors && this.validationErrors.includes(field);
    },
    clickOnAmortizationType(type) {
      this.amortizationType = type;
      this.isDisabledSaveBtn = false;
    },
    async getExcelEventsReport() {
      this.error = null;
      try {
        this.$loadingManager.addLoadingStep();
        const data = {
          lesseeId: this.formData.lesseeId,
          periodStart: this.formData.periodStart,
          periodEnd: this.formData.periodEnd,
        };

        const api = new ReportsAPI();
        const response = await api.getReportBasedOnEvents(data);
        this.$loadingManager.loadingStepLoaded()
        let filename = null;
        let filenameInURI = null;

        let headerContentDisposition = response.headers['content-disposition'].split(';');
        for (let i = 0; i < headerContentDisposition.length; i++) {
          let item = headerContentDisposition[i].split('=');
          if (item[0].trim() === 'filename*') {
            filenameInURI = item[1].split("''");
            filename = decodeURIComponent(filenameInURI[1]);
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.error = e;
        this.$loadingManager.hideLoadScreen();
        console.error(e);
      }
    },
    async onSubmit() {
      try {
        this.error = null;
        this.$loadingManager.addLoadingStep();
        await this.$store.dispatch('setContractSettings', {
          currencyCode: this.currencyCode,
          yearsToAddToPerpetuity: this.yearsToAddToPerpetuity,
          amortizationType: this.amortizationType
        });
        this.$loadingManager.loadingStepLoaded();
      } catch (e) {
        this.$loadingManager.hideLoadScreen();
        this.error = e;
      }
    },
    async validateRegularPayments() {
      this.error = null;
      try {
        const originalType = this.importType;
        this.importType = 'importInvalidValidationContracts';
        await this.downloadTemplate();
        this.importType = originalType;

      } catch (e) {
        this.error = e;
        console.error(e);
      }
    },
    async getLastLogs() {
      this.error = null;
      try {
        let response = null;
        const api = new LogAPI();
        response = await api.getLast();

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `lastLogs.json`);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.error = e;
        console.error(e);
      }
    },
    async getAllLogs() {
      this.error = null;
      try {
        let response = null;
        const api = new LogAPI();
        response = await api.getAll();

        var blob = new Blob([response]);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `allLogs.zip`);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        this.error = e;
        console.error(e);
      }
    },
    async getClosedPeriodLogs() {
      this.error = null;
      try {
        const api = new LogAPI();
        const response = await api.getClosedPeriodLogs();

        if (!(response instanceof Blob)) {
          throw new Error('Expected Blob response');
        }
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'closed_period_logs.json');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = 'Failed to download logs. Please try again.';
        console.error('Error downloading closed period logs:', e);
      }
    }
  }
}
</script>
